import { useCallback, useState } from 'react';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { reprocessForReingestion } from '../../../services/queries/MaggieReingestionQueries';
import { getErrorMessage } from '../../../services/queryHelpers';

export type ResetEntityTab = 'companies' | 'funds';

export interface SelectedEntity {
  id: number;
  name: string;
}

export function useResetReingestion() {
  const { pushSuccessToast, pushInfoToast, pushErrorToast } = useToastMessageState();
  const [activeTab, setActiveTab] = useState<ResetEntityTab>('companies');
  const [selected, setSelected] = useState<SelectedEntity[]>([]);
  const [reloadImmediately, setReloadImmediately] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Selections are per-tab ("companies OR funds"); switching tabs remounts the grid, so clear too.
  const onTabChange = useCallback((tab: ResetEntityTab) => {
    setActiveTab(tab);
    setSelected([]);
  }, []);

  const onConfirm = useCallback(async () => {
    if (selected.length === 0) {
      return;
    }
    setLoading(true);
    try {
      const ids = selected.map((e) => e.id);
      const scope = activeTab === 'companies' ? { companyIds: ids } : { fundIds: ids };
      const result = await reprocessForReingestion(scope, reloadImmediately);
      if (result.rowsMarked === 0) {
        pushInfoToast({ message: result.message });
      } else {
        pushSuccessToast({ message: result.message });
      }
      setConfirmOpen(false);
      setSelected([]);
    } catch (err) {
      pushErrorToast({
        message: getErrorMessage(err, 'Failed to reset ledger transactions for re-ingestion'),
      });
    } finally {
      setLoading(false);
    }
  }, [selected, activeTab, reloadImmediately, pushSuccessToast, pushInfoToast, pushErrorToast]);

  return {
    activeTab,
    onTabChange,
    selected,
    setSelected,
    reloadImmediately,
    setReloadImmediately,
    confirmOpen,
    setConfirmOpen,
    loading,
    onConfirm,
  };
}
